import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiHelpCircle, FiPlus, FiTrash2, FiSave, FiX, FiMessageSquare,
  FiTag, FiFileText
} from 'react-icons/fi';

import { getFaq, storeFaq, storeGritting } from "../../../redux/services/faqService/faq.Services";
import { toast } from '../../../Utils/toast';
import { errorToast, successToast, warningToast } from '../../../Utils/toast';

const emptyFaq = () => ({
  question: '',
  answer: '',
  keywords: [],
  keywordInput: '',
  _id: Date.now() + Math.random(),
});

const parseKeywords = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [String(parsed)];
  } catch {
    return String(raw).split(/\s+/).filter(Boolean);
  }
};

export default function FAQ() {
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');
  const [closingQuestion, setClosingQuestion] = useState('');
  const [faqs, setFaqs] = useState([emptyFaq()]);
  const [loading, setLoading] = useState(true);

  const [savingGreeting, setSavingGreeting] = useState(false);
  const [savingFaqs, setSavingFaqs] = useState(false);

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    try {
      setLoading(true);
      const res = await getFaq();

      // Greeting + Closing
      const greetingData = res?.vendor_greeting || res?.data?.vendor_greeting || {};
      setGreeting(greetingData.greeting || '');
      setClosingQuestion(greetingData.closing_question || '');

      // FAQs list
      const rawList =
        Array.isArray(res?.faqs) ? res.faqs :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.data?.faqs) ? res.data.faqs :
        [];

      if (rawList.length > 0) {
        const parsed = rawList.map((item, idx) => ({
          question: item.question || '',
          answer: item.answer || '',
          keywords: parseKeywords(item.keywords),
          keywordInput: '',
          _id: item.id ?? item._id ?? (Date.now() + idx),
        }));
        setFaqs(parsed);
      } else {
        setFaqs([emptyFaq()]);
      }
    } catch (err) {
      console.error('Error loading FAQs:', err);
      errorToast(err?.response?.data?.message || 'Failed to load FAQs');
      setFaqs([emptyFaq()]);
    } finally {
      setLoading(false);
    }
  };

  const addFaq = () => setFaqs(prev => [emptyFaq(),...prev]);
  const removeFaq = (id) => {
    // if (faqs.length === 1) {
    //   warningToast('At least one FAQ is required');
    //   return;
    // }
    setFaqs(prev => prev.filter(f => f._id !== id));
  };

  const updateFaq = (id, key, value) => {
    setFaqs(prev =>
      prev.map(f => (f._id === id ? { ...f, [key]: value } : f))
    );
  };

  const addKeyword = (id) => {
    const faq = faqs.find(f => f._id === id);
    if (!faq) return;
    const kw = (faq.keywordInput || '').trim();
    if (!kw) {
      warningToast('Please enter a keyword');
      return;
    }
    const newKeywords = kw.split(/\s+/).filter(Boolean);
    updateFaq(id, 'keywords', [...faq.keywords, ...newKeywords]);
    updateFaq(id, 'keywordInput', '');
  };

  const removeKeyword = (faqId, kwIndex) => {
    const faq = faqs.find(f => f._id === faqId);
    if (!faq) return;
    const newKeywords = faq.keywords.filter((_, i) => i !== kwIndex);
    updateFaq(faqId, 'keywords', newKeywords);
  };

  // ✅ Save Greeting Only
  const handleSaveGreeting = async () => {
    if (!greeting.trim()) {
      errorToast('Greeting is required');
      return;
    }
    if (!closingQuestion.trim()) {
      errorToast('Closing Question is required');
      return;
    }

    const greetingPayload = {
      greeting: greeting.trim(),
      closing_question: closingQuestion.trim(),
    };

    try {
      setSavingGreeting(true);
      const res = await storeGritting(greetingPayload);

      if (res?.success === false || res?.status === 'error') {
        throw new Error(res?.message || 'Failed to save greeting');
      }

      successToast(res?.message || 'Greeting saved successfully!');
      await loadFaqs();
    } catch (err) {
      console.error('Error saving greeting:', err);
      errorToast(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save greeting'
      );
    } finally {
      setSavingGreeting(false);
    }
  };

  // ✅ Save FAQs Only
  const handleSaveFaqs = async () => {
    if (faqs.length === 0) {
      errorToast('At least one FAQ is required');
      return;
    }

    for (let i = 0; i < faqs.length; i++) {
      if (!faqs[i].question.trim() || !faqs[i].answer.trim()) {
        errorToast(`FAQ ${i + 1}: Question and Answer are required`);
        return;
      }
    }

    const faqPayload = {
      faqs: faqs.map(f => ({
        question: f.question.trim(),
        keywords: f.keywords,
        answer: f.answer.trim(),
      })),
    };

    try {
      setSavingFaqs(true);
      const res = await storeFaq(faqPayload);

      if (res?.success === false || res?.status === 'error') {
        throw new Error(res?.message || 'Failed to save FAQs');
      }

      successToast(res?.message || 'FAQs saved successfully!');
      await loadFaqs();
    } catch (err) {
      console.error('Error saving FAQs:', err);
      errorToast(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save FAQs'
      );
    } finally {
      setSavingFaqs(false);
    }
  };

  if (loading) {
    return <div className="pg-state">Loading FAQs...</div>;
  }

  return (
    <>
      {/* Header */}
      <div className="dt-header">
        <div className="dt-header-left">
          <div className="dt-header-icon"><FiHelpCircle /></div>
          <div>
            <h1>Manage FAQs</h1>
            <p>Configure greeting, closing question and FAQs for your AI agent.</p>
          </div>
        </div>
      </div>

      <div className="pg-form">
        {/* ---------- Greeting + Closing ---------- */}
        <div className="faq-header" style={{ marginTop: 0 }}>
          <div className="pg-section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
            <FiMessageSquare /> Greeting & Closing
          </div>
          <button
            type="button"
            className="dt-btn dt-btn-primary"
            onClick={handleSaveGreeting}
            disabled={savingGreeting}
          >
            <FiSave /> {savingGreeting ? 'Saving...' : 'Save Greeting'}
          </button>
        </div>

        <div className="pg-form-row">
          <label>Greeting
            <textarea
              rows={3}
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              placeholder="Hello, thank you for contacting A2 Technosoft..."
              style={{ resize: 'vertical', minHeight: 90 }}
            />
          </label>

          <label>Closing Question
            <textarea
              rows={3}
              value={closingQuestion}
              onChange={(e) => setClosingQuestion(e.target.value)}
              placeholder="Thank you"
              style={{ resize: 'vertical', minHeight: 90 }}
            />
          </label>
        </div>

        {/* ---------- FAQs ---------- */}
        <div className="faq-header" style={{ marginTop: 24 }}>
          <div className="pg-section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
            <FiHelpCircle /> FAQs ({faqs.length})
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="dt-btn dt-btn-outline" onClick={addFaq}>
              <FiPlus /> Add FAQ
            </button>
            <button
              type="button"
              className="dt-btn dt-btn-primary"
              onClick={handleSaveFaqs}
              disabled={savingFaqs}
            >
              <FiSave /> {savingFaqs ? 'Saving...' : 'Save FAQs'}
            </button>
          </div>
        </div>

        {faqs.map((faq, idx) => (
          <div className="faq-block" key={faq._id}>
            <div className="faq-block-head">
              <div className="faq-block-title">
               <FiFileText /> FAQ {faqs.length - idx}
              </div>
              {faqs.length > 1 && (
                <button
                  type="button"
                  className="faq-remove-btn"
                  onClick={() => removeFaq(faq._id)}
                  title="Remove FAQ"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>

            <div className="pg-form-row">
              <label>Question
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => updateFaq(faq._id, 'question', e.target.value)}
                  placeholder="Do you provide website development services?"
                />
              </label>

              <label>Answer
                <textarea
                  rows={3}
                  value={faq.answer}
                  onChange={(e) => updateFaq(faq._id, 'answer', e.target.value)}
                  placeholder="Yes, A2 Technosoft provides..."
                  style={{ resize: 'vertical', minHeight: 90 }}
                />
              </label>
            </div>

            <label>Keywords
              <input
                type="text"
                value={faq.keywordInput}
                onChange={(e) => updateFaq(faq._id, 'keywordInput', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addKeyword(faq._id);
                  }
                }}
                placeholder="Type keywords (space-separated) and press Enter"
              />
            </label>

            {faq.keywords.length > 0 && (
              <div className="faq-keywords">
                {faq.keywords.map((kw, i) => (
                  <span className="faq-keyword-chip" key={i}>
                    <FiTag /> {kw}
                    <button
                      type="button"
                      onClick={() => removeKeyword(faq._id, i)}
                      title="Remove keyword"
                    >
                      <FiX />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <button
              type="button"
              className="dt-btn dt-btn-outline faq-add-keyword"
              onClick={() => addKeyword(faq._id)}
            >
              <FiPlus /> Add Keyword
            </button>
          </div>
        ))}

        {/* Bottom Save FAQs (optional duplicate for convenience) */}
        <div className="pg-form-footer">
          <button
            type="button"
            className="dt-btn dt-btn-ghost"
            onClick={() => navigate('/')}
            disabled={savingGreeting || savingFaqs}
          >
            <FiX /> Cancel
          </button>
          {/* <button
            type="button"
            className="dt-btn dt-btn-primary"
            onClick={handleSaveFaqs}
            disabled={savingFaqs}
          >
            <FiSave /> {savingFaqs ? 'Saving...' : 'Save FAQs'}
          </button> */}
        </div>
      </div>
    </>
  );
}